import { TaxProcedure } from '@carvia/ros-client-types';
import { isEuCountryCode } from '@/utils/eu-countries';
import {
  isLongRental,
  normalizeCountryCode,
  type TaxLegalEntity,
} from '@/utils/resolve-tax-country';
import { normalizeVatId } from '@/utils/vat-id';

/** Mirrors resolveDefaultBookingTaxProcedure in the backend. */
export function resolveDefaultBookingTaxProcedure(params: {
  taxCountry: string;
  vatId?: string | null;
  startDate: Date | string;
  endDate: Date | string;
  customerCountry?: string | null;
  handoverCountry?: string | null;
  legalEntity: TaxLegalEntity;
}): TaxProcedure {
  if (!params.legalEntity.isOssRegistered) {
    return resolveLegacyDefaultBookingTaxProcedure(params);
  }
  const taxCountry = params.taxCountry.trim().toUpperCase();

  // Private customers always pay the tax country's VAT; only business customers go net.
  if (
    taxCountry === params.legalEntity.countryCode.trim().toUpperCase() ||
    !normalizeVatId(params.vatId)
  ) {
    return TaxProcedure.REGULAR;
  }
  return isEuCountryCode(taxCountry) ? TaxProcedure.REVERSE_CHARGE : TaxProcedure.EXPORT;
}

/**
 * Suggestion for legal entities without OSS registration. Business customers go net when the place
 * of supply is abroad: where the customer lives for long rentals, where the car is handed over for
 * short ones.
 */
function resolveLegacyDefaultBookingTaxProcedure(params: {
  vatId?: string | null;
  startDate: Date | string;
  endDate: Date | string;
  customerCountry?: string | null;
  handoverCountry?: string | null;
  legalEntity: TaxLegalEntity;
}): TaxProcedure {
  const normalizedVatId = normalizeVatId(params.vatId);
  if (!normalizedVatId) {
    return TaxProcedure.REGULAR;
  }
  const placeOfSupply = isLongRental(params.startDate, params.endDate)
    ? (params.customerCountry ?? normalizedVatId.slice(0, 2)).trim().toUpperCase()
    : normalizeCountryCode(params.handoverCountry);
  if (!placeOfSupply || placeOfSupply === params.legalEntity.countryCode.trim().toUpperCase()) {
    return TaxProcedure.REGULAR;
  }
  return isEuCountryCode(placeOfSupply) ? TaxProcedure.REVERSE_CHARGE : TaxProcedure.EXPORT;
}
